import {useFunctionMapSelector} from "../../../../../../store/selectors/project.selectors";
import React, {useMemo, useState} from "react";
import {useProjectMemberListSelector} from "../../../../../../store/selectors/team.selectors";
import FirebaseUsage from "../../../../../../firebase/firebase.usage";
import {COLLECTIONS} from "../../../../../../firebase/constants";
import {Button, Dropdown, Modal} from "semantic-ui-react";
import styles from "./FunctionModal.module.scss"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCopy, faLock, faLockOpen, faPlus, faTrash} from "@fortawesome/pro-regular-svg-icons";
import {FunctionModel} from "../../../../../../models/responses/function.model";

export default function FunctionsModal(props: {
    isOpen: boolean,
    setOpen: any,
    clientId: string,
}) {
    const { isOpen, setOpen, clientId } = props;
    const [selectedFunction, setSelectedFunction] = useState<FunctionModel | null>(null);
    const [selectedFunctionItem, setSelectedFunctionItem] = useState<any | null>(null);
    const [addingFunction, setAddingFunction] = useState(false);
    const [addingFunctionItem, setAddingFunctionItem] = useState(false);
    const [editFunctionId, setEditFunctionId] = useState<string | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const projectMemberList = useProjectMemberListSelector();
    const functionMap = useFunctionMapSelector();
    const [updateCount, setUpdateCount] = useState(0);

    const addNewFunction = async (name: string) => {
        if (name === "") return;
        const newFunctionDoc = FirebaseUsage.getBlankDoc(COLLECTIONS.FUNCTIONS);
        const newFunction: FunctionModel = {
            functionId: newFunctionDoc.id,
            functionName: name,
            clientId: clientId,
            users: [],
        }
        await FirebaseUsage.setDocumentWithDoc(newFunctionDoc, newFunction);
        functionMap.set(newFunction.functionId, newFunction);
        setUpdateCount(updateCount + 1);
    }

    const deleteFunction = async () => {
        if (!selectedFunction) return;
        await FirebaseUsage.deleteDocument(COLLECTIONS.FUNCTIONS, selectedFunction.functionId);
        functionMap.delete(selectedFunction.functionId);
        setUpdateCount(updateCount + 1);
    }

    const functions = useMemo(() => {
        return Array.from(functionMap.values())
            .sort((a: FunctionModel, b: FunctionModel) => a.functionName.localeCompare(b.functionName))
    }, [functionMap, updateCount]);

    const renameFunction = async (name: string, functionId: string) => {
        if (name === "") return;
        await FirebaseUsage.updateDoc(COLLECTIONS.FUNCTIONS, functionId, {
            functionName: name,
        });
        const updatedFunction = {
            ...functionMap.get(functionId),
            functionName: name,
        } as FunctionModel;
        functionMap.set(functionId, updatedFunction);
        setSelectedFunction(updatedFunction);
        setUpdateCount(updateCount + 1);
    }

    const addNewFunctionMember = async (name: string) => {
        if (!selectedFunction) return;
        const updatedFunction = {
            ...selectedFunction,
            users: [...selectedFunction.users, name],
        } as FunctionModel;
        await FirebaseUsage.updateDoc(COLLECTIONS.FUNCTIONS, selectedFunction.functionId, {
            users: updatedFunction.users,
        });
        functionMap.set(selectedFunction.functionId, updatedFunction);
        setUpdateCount(updateCount + 1);
        setSelectedFunction(updatedFunction);
        setSelectedFunctionItem(name);
        setAddingFunctionItem(false);
    }

    const memberOptions = useMemo(() => {
        return projectMemberList.map((member) => ({
            key: member.memberId,
            text: member.userEmail,
            value: member.userEmail,
        })).filter(option =>
            !selectedFunction || !selectedFunction.users.includes(option.value));
    }, [projectMemberList, selectedFunction]);

    const copyFunction = async () => {
        if (!selectedFunction) return;
        const newFunctionDoc = FirebaseUsage.getBlankDoc(COLLECTIONS.FUNCTIONS);
        const newFunction: FunctionModel = {
            functionId: newFunctionDoc.id,
            functionName: selectedFunction.functionName + " Copy",
            clientId: clientId,
            users: [...selectedFunction.users],
        }
        await FirebaseUsage.setDocumentWithDoc(newFunctionDoc, newFunction);
        functionMap.set(newFunction.functionId, newFunction);
        setSelectedFunction(newFunction);
        setUpdateCount(updateCount + 1);
    }

    const deleteFunctionMember = async () => {
        if (!selectedFunction || !selectedFunctionItem) return;
        const updatedFunction = {
            ...selectedFunction,
            users: selectedFunction.users.filter(user => user !== selectedFunctionItem),
        } as FunctionModel;
        await FirebaseUsage.updateDoc(COLLECTIONS.FUNCTIONS, selectedFunction.functionId, {
            users: updatedFunction.users,
        });
        functionMap.set(selectedFunction.functionId, updatedFunction);
        setSelectedFunction(updatedFunction);
        setSelectedFunctionItem(null);
        setUpdateCount(updateCount + 1);
    }

    return (
        <Modal open={isOpen} onClose={() => {
            setOpen(false)
        }} dimmer={'blurring'} size={'large'} closeIcon closeOnEscape={false}
        >
            <Modal.Header><h3>Function Library</h3></Modal.Header>
            <Modal.Content>
                <div className={styles.ModalLabels}>
                    <p></p>
                    <label><strong>Functions</strong></label>
                    <label><strong>Function Members</strong></label>
                    <p></p>
                </div>
                <div className={styles.ModalContainer}>
                    <div className={styles.FunctionActions}>
                        <div className={styles.ActionButton} onClick={() => setAddingFunction(true)}>
                            <FontAwesomeIcon icon={faPlus} size="lg" />
                        </div>
                        <div className={functions.length && selectedFunction ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => {
                                 if (selectedFunction) {
                                     copyFunction().catch(e => console.error(e));
                                 }
                             }}>
                            <FontAwesomeIcon icon={faCopy} size="lg" />
                        </div>
                        <div className={functions.length && selectedFunction ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => setOpenDeleteModal(true)}>
                            <FontAwesomeIcon icon={faTrash} size="lg" />
                        </div>
                    </div>
                    <div className={styles.ExistingFunctions}>
                        {addingFunction &&
                            <input
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        if (name) {
                                            addNewFunction(name).then(() => setAddingFunction(false));
                                        }
                                    } else if (e.key === 'Escape') {
                                        setAddingFunction(false);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (e.target.value) {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        addNewFunction(name).then(() => setAddingFunction(false));
                                    } else {
                                        setAddingFunction(false)
                                    }
                                }}
                                className={styles.NewFunctionInput}
                                type="text"
                                placeholder="New Function" />
                        }
                        {functions.map((fn, index) => editFunctionId && editFunctionId === fn.functionId ? (
                            <input
                                autoFocus
                                key={index}
                                placeholder={fn.functionName}
                                className={styles.NewFunctionInput}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        if (name) {
                                            renameFunction(name, fn.functionId).then(() => setEditFunctionId(null));
                                        }
                                    } else if (e.key === 'Escape') {
                                        setEditFunctionId(null);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (e.target.value) {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        renameFunction(name, fn.functionId).then(() => setEditFunctionId(null));
                                    } else {
                                        setEditFunctionId(null);
                                    }
                                }}
                            />
                        ) : (
                            <div key={index} className={styles.FunctionEntry}
                                 style={selectedFunction?.functionId === fn.functionId ? {
                                     backgroundColor: 'rgba(203, 230, 255, 0.7)',
                                     fontWeight: 'bold',
                                 } : {}}
                                 onDoubleClick={() => setEditFunctionId(fn.functionId)}
                                 onClick={() => setSelectedFunction(selectedFunction && selectedFunction.functionId === fn.functionId ? null : fn)}>
                                {fn.functionName}
                            </div>
                        ))}
                    </div>
                    <div className={styles.EditFunction} style={selectedFunction ? {} : {backgroundColor: 'rgba(241,239,239,0.4)'}}>
                        {addingFunctionItem  && selectedFunction && <div className={styles.FunctionItemInput}>
                            <Dropdown
                                options={memberOptions}
                                placeholder='Select Member'
                                search
                                selection
                                fluid
                                onChange={(e, { value }) => {
                                    if (value && typeof value === 'string') {
                                        addNewFunctionMember(value)
                                            .then(() => setAddingFunctionItem(false))
                                            .catch(e => console.error(e));
                                    }
                                }}
                            />
                        </div>}
                        {selectedFunction && selectedFunction.users.length > 0 &&
                            selectedFunction.users.map((item: string, index) => (
                                <div key={`item-${index}`} className={styles.FunctionItemEntry}
                                     style={selectedFunctionItem && selectedFunctionItem === item? {
                                         backgroundColor: 'rgba(203, 230, 255, 0.7)',
                                         fontWeight: 'bold',
                                     } : {}}
                                     onClick={() => setSelectedFunctionItem(item)}
                                >
                                    <div className={styles.FunctionItemName}>{item}</div>
                                </div>
                            ))}
                    </div>
                    <div className={styles.FunctionItemActions}>
                        <div className={selectedFunction && selectedFunction ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => setAddingFunctionItem(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} size="lg" />
                        </div>
                        <div className={selectedFunction && selectedFunction.users.length && selectedFunctionItem ?
                            styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => {
                                 if (selectedFunctionItem && selectedFunction) {
                                     deleteFunctionMember().catch(e => console.error(e));
                                 }
                             }}>
                            <FontAwesomeIcon icon={faTrash} size="lg" />
                        </div>
                        {/*<div className={selectedFunction && checkListItems.length ? styles.ActionButton : styles.ActionButtonDisabled}*/}
                        {/*     onClick={() => {*/}
                        {/*         if (selectedFunction && selectedFunction && checkListItems.length) {*/}
                        {/*             setOpenLockModal(true);*/}
                        {/*         }}}>*/}
                        {/*    <FontAwesomeIcon icon={selectedFunction ? faLockOpen : faLock} size="lg" />*/}
                        {/*</div>*/}
                    </div>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => {
                    setOpen(false);
                }}>Close</Button>
            </Modal.Actions>
            <Modal size={'mini'} open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <Modal.Header>Delete Function</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this function?</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
                    <Button negative onClick={() => {
                        deleteFunction().catch(e => console.error(e));
                        setOpenDeleteModal(false);
                    }}>Delete</Button>
                </Modal.Actions>
            </Modal>
        </Modal>
    )
}